import { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Anchor, CalendarDays, BookOpen, KeyRound,
  Settings, LogOut, Menu, X, Ship, Users, LayoutGrid,
  ShieldCheck, BarChart2, Package, CircleDollarSign, Layers, Car, Coins,
  ListChecks,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ToastContainer } from './Toast';
import { ErrorBoundary } from './ErrorBoundary';
import { MarinaSwitcher } from './MarinaSwitcher';
import { api } from '../lib/api';
import { useParkingModule } from '../hooks/useParkingModule';
import { useShowOnboardingNav } from '../hooks/useShowOnboardingNav';
import { setLanguage, getLanguage } from '../i18n';

interface Marina {
  id: number;
  name: string;
}

export function Layout() {
  const { t } = useTranslation();
  const { signOut, user, profile, isOrgAdmin, activeMarinaId } = useAuth();
  const { parkingEnabled } = useParkingModule();
  const showOnboardingNav = useShowOnboardingNav();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orgMarinas, setOrgMarinas] = useState<Marina[]>([]);
  const [lang, setLang] = useState<'et' | 'en'>(getLanguage());

  useEffect(() => {
    if (isOrgAdmin) {
      api.getOrgMarinas()
        .then(data => setOrgMarinas((data.marinas ?? []) as unknown as Marina[]))
        .catch(() => {/* non-critical */});
    }
  }, [isOrgAdmin]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleLangToggle = () => {
    const next = lang === 'et' ? 'en' : 'et';
    setLanguage(next);
    setLang(next);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-brand-600 text-white'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const NAV_ITEMS = useMemo(() => {
    const base: { to: string; icon: typeof LayoutDashboard; label: string }[] = [
      { to: '/', icon: LayoutDashboard, label: t('nav.dashboard') },
    ];
    if (showOnboardingNav) {
      base.push({ to: '/onboarding', icon: ListChecks, label: t('nav.onboarding') });
    }
    base.push(
      { to: '/analytics', icon: BarChart2, label: t('nav.analytics') },
      { to: '/bookings', icon: CalendarDays, label: t('nav.bookings') },
      { to: '/berths', icon: Anchor, label: t('nav.berths') },
      { to: '/pricing', icon: CircleDollarSign, label: t('nav.pricing') },
    );
    if (parkingEnabled) {
      base.push(
        { to: '/parking-spots', icon: Car, label: t('nav.parkingSpots') },
        { to: '/parking-pricing', icon: Coins, label: t('nav.parkingPricing') },
      );
    }
    base.push(
      { to: '/knowledge', icon: BookOpen, label: t('nav.knowledge') },
      { to: '/access-codes', icon: KeyRound, label: t('nav.accessCodes') },
      { to: '/modules', icon: Layers, label: t('nav.modules') },
      { to: '/additional-services', icon: Package, label: t('nav.additionalServices') },
      { to: '/settings', icon: Settings, label: t('nav.settings') },
    );
    return base;
  }, [t, parkingEnabled, showOnboardingNav]);

  const sidebar = (
    <nav className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-4 py-5 border-b border-gray-200">
        <Ship className="w-7 h-7 text-brand-600" />
        <span className="text-base font-bold text-gray-900">Harbormaster</span>
      </div>

      {/* Org-level navigation (org_admin+) */}
      {isOrgAdmin && (
        <div className="px-3 pt-3 pb-1 space-y-1 border-b border-gray-100">
          <NavLink
            to="/org"
            className={navLinkClass}
            onClick={() => setMobileOpen(false)}
          >
            <LayoutGrid className="w-[18px] h-[18px] shrink-0" />
            {t('nav.allMarinas')}
          </NavLink>
          <NavLink
            to="/team"
            className={navLinkClass}
            onClick={() => setMobileOpen(false)}
          >
            <Users className="w-[18px] h-[18px] shrink-0" />
            {t('nav.team')}
          </NavLink>
        </div>
      )}

      {/* Superadmin nav section */}
      {profile?.role === 'platform_superadmin' && (
        <div className="px-3 pt-3 pb-1 space-y-1 border-b border-gray-100">
          <p className="px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('nav.platform')}</p>
          {[
            { to: '/superadmin', label: t('nav.overview') },
            { to: '/superadmin/orgs', label: t('nav.organizations') },
            { to: '/superadmin/marinas', label: t('nav.allMarinas') },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/superadmin'}
              className={navLinkClass}
              onClick={() => setMobileOpen(false)}
            >
              <ShieldCheck className="w-[18px] h-[18px] shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}

      {/* Team link for marina_admin */}
      {!isOrgAdmin && profile?.role === 'marina_admin' && (
        <div className="px-3 pt-3 pb-1 space-y-1 border-b border-gray-100">
          <NavLink
            to="/team"
            className={navLinkClass}
            onClick={() => setMobileOpen(false)}
          >
            <Users className="w-[18px] h-[18px] shrink-0" />
            {t('nav.team')}
          </NavLink>
        </div>
      )}

      {/* Marina-level navigation */}
      <div className="flex-1 px-3 py-3 space-y-1 overflow-y-auto scrollbar-thin">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={navLinkClass}
            onClick={() => setMobileOpen(false)}
          >
            <item.icon className="w-[18px] h-[18px] shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </div>

      <div className="border-t border-gray-200 px-3 py-3">
        <div className="px-3 mb-0.5 text-xs text-gray-400 truncate">{profile?.name ?? user?.email}</div>
        <div className="px-3 mb-2 text-xs text-gray-300 truncate capitalize">{profile?.role?.replace(/_/g, ' ')}</div>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full"
        >
          <LogOut className="w-[18px] h-[18px]" />
          {t('nav.signOut')}
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 bg-white border-r border-gray-200">
        {sidebar}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl z-50">
            {sidebar}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 lg:pl-60">
        {/* Top bar with marina switcher */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 h-14">
            {/* Mobile: hamburger */}
            <button onClick={() => setMobileOpen(true)} className="p-1.5 -ml-1.5 text-gray-500 lg:hidden" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            {/* Mobile: logo */}
            <div className="flex items-center gap-2 lg:hidden">
              <Ship className="w-5 h-5 text-brand-600" />
              <span className="text-sm font-bold text-gray-900">Harbormaster</span>
            </div>

            {/* Desktop left: empty spacer */}
            <div className="hidden lg:block" />

            {/* Right: language switcher + marina switcher */}
            <div className="flex items-center gap-3">
              {/* Language toggle */}
              <button
                onClick={handleLangToggle}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                title={lang === 'et' ? 'Switch to English' : 'Vaheta eesti keelele'}
              >
                {lang === 'et' ? 'ET' : 'EN'}
                <span className="text-gray-300 mx-0.5">|</span>
                {lang === 'et' ? 'EN' : 'ET'}
              </button>

              {isOrgAdmin && orgMarinas.length > 0 && (
                <MarinaSwitcher marinas={orgMarinas} />
              )}
              {mobileOpen ? (
                <button onClick={() => setMobileOpen(false)} className="p-1.5 -mr-1.5 text-gray-500 lg:hidden" aria-label="Close menu">
                  <X className="w-5 h-5" />
                </button>
              ) : (
                <div className="w-8 lg:hidden" aria-hidden="true" />
              )}
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <ErrorBoundary>
            <Outlet key={activeMarinaId ?? 'all'} />
          </ErrorBoundary>
        </main>
      </div>

      <ToastContainer />
    </div>
  );
}
